import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileSpreadsheet, Layers, Plus, Printer } from 'lucide-react'
import { toast } from 'sonner'

import type { ListLotsParams } from '@/lib/api/product-lot-api'
import { productLotApi } from '@/lib/api/product-lot-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { positionFilter } from '@/components/common/filters/position-filter'
import { productFilter } from '@/components/common/filters/product-filter'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { toggleFilter } from '@/components/common/filters/toggle-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useWarehouses } from '@/hooks/use-inventory-query'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePrintProductLotLabel } from '@/hooks/use-lot-label-print'
import { useProduct } from '@/hooks/use-product-query'
import {
    useProductLotsInfiniteQuery,
    useProductLotsQuery,
} from '@/hooks/use-product-lot-query'
import { useWarehousePosition } from '@/hooks/use-warehouse-position-query'
import { formatDateTimeVN, formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Ba loại lọc theo id (sản phẩm, kho, vị trí) không suy ra được nhãn chip từ
 * giá trị — tên nằm ở server. Màn tra tên rồi truyền xuống qua `chipLabels`.
 */
const FILTERS = defineListFilters({
    product_id: productFilter({
        label: m.product_lot_list_filter_product,
        placeholder: m.product_lot_list_filter_all_products,
    }),
    warehouse_id: remoteSelectFilter({
        label: m.product_lot_list_filter_warehouse,
        allLabel: m.product_lot_list_filter_all_warehouses,
        useOptions: () => {
            const { data } = useWarehouses()
            return (data ?? []).map((w) => ({ value: w.id, label: w.name }))
        },
    }),
    position_id: positionFilter({
        label: m.lot_list_filter_position,
        lotType: 'product',
        placeholder: m.lot_list_filter_all_positions,
    }),
    only_available: toggleFilter({
        label: m.product_lot_list_filter_only_available,
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/lots/',
)({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

function Page() {
    const navigate = useNavigate({ from: '/sales-warehouse/lots/' })
    const urlSearch = Route.useSearch()

    // Ba bộ lọc theo id: URL chỉ có id, chip cần TÊN → tra runtime rồi truyền
    // xuống; chưa tra được thì không sinh chip (xem `productFilter`).
    const productId =
        typeof urlSearch.product_id === 'string' ? urlSearch.product_id : ''
    const positionId =
        typeof urlSearch.position_id === 'string' ? urlSearch.position_id : ''
    const warehouseId =
        typeof urlSearch.warehouse_id === 'string' ? urlSearch.warehouse_id : ''
    const { data: selectedProduct } = useProduct(productId)
    const { data: selectedPosition } = useWarehousePosition(
        positionId || undefined,
    )
    const { data: warehouses } = useWarehouses()
    const warehouseName = (warehouses ?? []).find(
        (w) => w.id === warehouseId,
    )?.name
    const positionLabel = selectedPosition?.data
        ? (selectedPosition.data.name ?? selectedPosition.data.code)
        : undefined

    const chipLabels = {
        product_id: selectedProduct
            ? `${m.product_lot_list_filter_product()}: ${selectedProduct.name}`
            : undefined,
        warehouse_id: warehouseName
            ? `${m.product_lot_list_filter_warehouse()}: ${warehouseName}`
            : undefined,
        position_id: positionLabel
            ? `${m.lot_list_filter_position()}: ${positionLabel}`
            : undefined,
    }

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
        chipLabels,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.product_id ? { product_id: values.product_id } : {}),
            ...(values.warehouse_id !== 'all'
                ? { warehouse_id: values.warehouse_id }
                : {}),
            ...(values.position_id ? { position_id: values.position_id } : {}),
            // `toggleFilter` trả string; API khai literal '1' nên chốt lại ở
            // đây — chỉ có đúng một giá trị bật.
            ...(values.only_available ? { only_available: '1' as const } : {}),
        }),
        [
            values.product_id,
            values.warehouse_id,
            values.position_id,
            values.only_available,
        ],
    )

    const params = useMemo<ListLotsParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )

    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useProductLotsQuery(
        params,
        { enabled: !isMobile },
    )

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useProductLotsInfiniteQuery(infiniteParams, {
        enabled: isMobile,
    })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const printMut = usePrintProductLotLabel()

    // Xuất Excel theo đúng filter đang áp (BE tự quét toàn bộ, bỏ qua page).
    const [exporting, setExporting] = useState(false)
    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await productLotApi.exportExcel(params)
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? 'lo-san-pham.xlsx',
            )
        } catch {
            toast.error(m.lot_list_export_failed())
        } finally {
            setExporting(false)
        }
    }

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.product_lot_list_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.product_lot_list_title()}
                utilityActions={
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleExport()}
                            disabled={exporting}
                            title={m.lot_list_export_btn()}
                            aria-label={m.lot_list_export_btn()}
                        >
                            <FileSpreadsheet className="size-4 md:mr-1" />
                            <span className="hidden md:inline">
                                {exporting
                                    ? m.lot_list_exporting()
                                    : m.lot_list_export_btn()}
                            </span>
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/sales-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pll_note_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileLots.map((lot) => {
                            const expiry = lot.expiry_date
                                ? formatDateVN(lot.expiry_date)
                                : null
                            const attributeSub = [
                                lot.warehouse_name,
                                lot.position_code,
                                expiry ? `HSD ${expiry}` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                            return (
                                <LotListCard
                                    key={lot.id}
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                    code={
                                        <span className="inline-flex items-center gap-1.5">
                                            <Link
                                                to="/sales-warehouse/lots/$id"
                                                params={{ id: lot.id }}
                                                className={LOT_CARD_CODE_CLASS}
                                            >
                                                {lot.code}
                                            </Link>
                                            {lot.discount_percent > 0 ? (
                                                <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                    CK {lot.discount_percent}%
                                                </span>
                                            ) : null}
                                        </span>
                                    }
                                    attribute={lot.product_name ?? '—'}
                                    attributeSub={
                                        attributeSub ? attributeSub : undefined
                                    }
                                    quantityAvailable={lot.quantity_available}
                                    createdAt={lot.created_at}
                                    actions={
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={m.lot_label_print_btn()}
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    }
                                />
                            )
                        })}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>
                                {m.product_lot_list_col_product()}
                            </TableHead>
                            <TableHead>
                                {m.product_lot_list_col_warehouse()}
                            </TableHead>
                            <TableHead>{m.lot_list_col_position()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.pkl_lots_col_expiry()}</TableHead>
                            <TableHead>{m.sl_lots_col_created_at()}</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/sales-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                        {lot.discount_percent > 0 ? (
                                            <span className="ml-1.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                CK {lot.discount_percent}%
                                            </span>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {lot.product_name ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {lot.warehouse_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {lot.position_code ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {lot.expiry_date
                                            ? formatDateVN(lot.expiry_date)
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
